// Has a new version of the site been rolled since this tab loaded?
//
// After a roll a tab keeps running the old bundle, and the API it talks to has
// moved on (operator, 2026-11-05: an old tab's wallet create got 400
// ownership_proof_required until a reload). The tab finds out by itself:
// index.html is served no-cache and names the entry chunk, whose file name is
// a hash of the code (Vite). A different entry in a fresh index.html than the
// one this page runs means a new version is out. No build input carries a
// version: the artifact is the version.
//
// Asked on focus and when the tab comes back (at most every 30 s), every
// 5 minutes, and at once on a sign of an old client: an "old client" API code
// (apiFetch) or a lazy chunk that is gone from the server (vite:preloadError).

const ENTRY_SELECTOR = 'script[type="module"][src*="/assets/"]'

/** The entry chunk a document names: /assets/index-<hash>.js. */
export function entryOf(doc: Pick<Document, 'querySelector'>): string | null {
  const src = doc.querySelector(ENTRY_SELECTOR)?.getAttribute('src') ?? null
  if (!src) return null
  try {
    return new URL(src, 'https://x.invalid').pathname
  } catch {
    return null
  }
}

/** The entry chunk an index.html names. */
export function entryOfHtml(html: string): string | null {
  for (const tag of html.match(/<script\b[^>]*>/gi) ?? []) {
    if (!/\btype=["']module["']/i.test(tag)) continue
    const src = /\bsrc=["']([^"']+)["']/i.exec(tag)?.[1]
    if (src?.includes('/assets/')) return new URL(src, 'https://x.invalid').pathname
  }
  return null
}

/** API error codes that mean "this client is older than the API". */
export const OLD_CLIENT_CODES: ReadonlySet<string> = new Set(['ownership_proof_required'])

export type SiteVersionWatcher = {
  /** Compare with the live index.html; resolves true once a new version is out. */
  check: (force?: boolean) => Promise<boolean>
  outdated: () => boolean
  subscribe: (listener: () => void) => () => void
}

export function createSiteVersionWatcher(deps: {
  running: string | null
  fetchIndex: () => Promise<string>
  now?: () => number
  minGapMs?: number
}): SiteVersionWatcher {
  const now = deps.now ?? Date.now
  const minGap = deps.minGapMs ?? 30_000
  let outdated = false
  let inFlight: Promise<boolean> | null = null
  let lastAt = Number.NEGATIVE_INFINITY
  const listeners = new Set<() => void>()

  const check = (force = false): Promise<boolean> => {
    // A page whose entry is unknown (the dev server) never reports one.
    if (outdated || deps.running === null) return Promise.resolve(outdated)
    if (inFlight) return inFlight
    if (!force && now() - lastAt < minGap) return Promise.resolve(false)
    lastAt = now()
    inFlight = deps
      .fetchIndex()
      .then((html) => {
        const live = entryOfHtml(html)
        // An answer that names no entry (an error page, a proxy) says nothing.
        if (live !== null && live !== deps.running) {
          outdated = true
          for (const listener of listeners) listener()
        }
        return outdated
      })
      .catch(() => false)
      .finally(() => {
        inFlight = null
      })
    return inFlight
  }

  return {
    check,
    outdated: () => outdated,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

let shared: SiteVersionWatcher | null = null

/** This page's watcher (production bundles only; in dev and tests it never
 *  reports a new version). */
export function siteVersion(): SiteVersionWatcher {
  if (!shared) {
    const running =
      import.meta.env.PROD && typeof document !== 'undefined' ? entryOf(document) : null
    shared = createSiteVersionWatcher({
      running,
      fetchIndex: async () => {
        const resp = await fetch('/index.html', { cache: 'no-store', credentials: 'omit' })
        if (!resp.ok) throw new Error(`index.html ${resp.status}`)
        return resp.text()
      },
    })
  }
  return shared
}

/** apiFetch saw an error code: one that means an old client asks right away. */
export function noteApiErrorCode(code: string): void {
  if (OLD_CLIENT_CODES.has(code)) void siteVersion().check(true)
}

/** Whether reloading now loses nothing the user is doing: nothing typed into,
 *  no dialog open, no trade under way, and the tab hidden or untouched for a
 *  while. */
export function quietForReload(input: {
  doc: Pick<Document, 'activeElement' | 'querySelector' | 'visibilityState'>
  idleMs: number
  tradeUnderWay: boolean
}): boolean {
  if (input.tradeUnderWay) return false
  const active = input.doc.activeElement as HTMLElement | null
  if (
    active &&
    (active.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName))
  ) {
    return false
  }
  if (input.doc.querySelector('dialog[open], [role="dialog"], [aria-modal="true"]')) return false
  return input.doc.visibilityState === 'hidden' || input.idleMs >= IDLE_RELOAD_MS
}

/** Untouched this long, a tab with a new version out reloads itself. */
export const IDLE_RELOAD_MS = 3 * 60_000
